import React, {useEffect, useMemo, useState} from "react";
import { motion, AnimatePresence } from "framer-motion";
import Loading from "../../ui/Loading";
import useApiGetBillingData from "./api-hooks/useApiGetBillingData";
import useApiUpdateBillingData from "./api-hooks/useApiUpdateBillingData";
import useApiGetPaymentMethods from "../../api-hooks/useApiGetPaymentMethods";
import BillingEditForm from "./components/BillingEditForm";
import {useNavigate} from "react-router-dom";
import ExpandIcon from "./components/ExpandIcon";
import {IoDownloadOutline, IoPeopleOutline} from "react-icons/io5";
import CreateBillingModal from "./components/CreateBillingModal";
import useApiCreateBillingData from "./api-hooks/useApiCreateBillingData";
import useApiGetListRestaurants from "./api-hooks/useApiGetListRestaurants";

function BillingDataPage() {
    const [expandedRows, setExpandedRows] = useState<Record<number, boolean>>({});
    const [editingId, setEditingId] = useState<number | null>(null);
    const [filter, setFilter] = useState<string>("");
    const [editError, setEditError] = useState<string | null>(null);
    const [form, setForm] = useState<{
        cisco_id: string | null;
        name: string;
        address: string | null;
        city: string | null;
        zip: string | null;
        province: string | null;
        vat: string | null;
        tax_id: string | null;
        payment_code: string;
        payment_method: string;
        unique_email: string | null
    }>({
        cisco_id: "",
        name: "",
        address: "",
        city: "",
        zip: "",
        province: "",
        vat: "",
        tax_id: "",
        payment_code: "",
        payment_method: "",
        unique_email: ""
    });

    const { data, isLoading, isError, error } = useApiGetBillingData({ enabled: true });

    const paymentMethodsQuery = useApiGetPaymentMethods({ enabled: true });
    const paymentMethods = paymentMethodsQuery.data;
    const isPmLoading = paymentMethodsQuery.isLoading;
    const isPmError = paymentMethodsQuery.isError;
    const pmList: any[] = Array.isArray(paymentMethods) ? paymentMethods : Object.values(paymentMethods || {});

    const updateBillingMutation = useApiUpdateBillingData(["getBillingData"]);
    const createBillingMutation = useApiCreateBillingData(["getBillingData"]);
    const restaurantsQuery = useApiGetListRestaurants({ enabled: true });
    const restaurants = restaurantsQuery.data || [];
    const isRestaurantsLoading = restaurantsQuery.isLoading;
    const isRestaurantsError = restaurantsQuery.isError;

    const toggleRow = (id: number) => {
        setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }));
    };

    const startEdit = (row: any) => {
        setEditingId(row.id);
        setEditError(null);
        setForm({
            cisco_id: row.cisco_id ?? "",
            name: row.company_name ?? "",
            address: row.address ?? "",
            city: row.city ?? "",
            zip: row.zip ?? "",
            province: row.province ?? "",
            vat: row.vat ?? "",
            tax_id: row.tax_id ?? "",
            payment_code: row.payment_code ?? "",
            payment_method: row.payment_method ?? "",
            unique_email: row.unique_email ?? ""
        });
    };

    const cancelEdit = () => {
        setEditingId(null);
        setEditError(null);
    };

    const filtered = useMemo(() => {
        const list = data || [];
        if (!filter.trim()) return list;
        const q = filter.trim().toLowerCase();
        return list.filter((x: any) =>
            (x.company_name || "").toLowerCase().includes(q) ||
            (x.contact_name || "").toLowerCase().includes(q)
        );
    }, [data, filter]);

    const submitEdit = (id: number) => {
        setEditError(null);
        if (!form.name?.toString().trim()) {
            setEditError("Il campo Ragione Sociale è obbligatorio.");
            return;
        }
        if (!form.payment_code?.toString().trim()) {
            setEditError("I campi Codice pagamento e Metodo pagamento sono obbligatori.");
            return;
        }
        const selectedPaymentMethod = paymentMethods?.find((pm: any) => pm.official_code === form.payment_code);
        const paymentCodeToSend = selectedPaymentMethod ? selectedPaymentMethod.code.toString() : form.payment_code;

        updateBillingMutation.mutate(
            {
                billing_id: id,
                cisco_id: form.cisco_id || null,
                name: form.name.trim(),
                address: form.address?.toString() || null,
                city: form.city?.toString() || null,
                zip: form.zip?.toString() || null,
                province: form.province?.toString() || null,
                vat: form.vat?.toString() || null,
                tax_id: form.tax_id?.toString() || null,
                payment_code: paymentCodeToSend,
                payment_method: form.payment_method,
                unique_email: (form.unique_email?.toString().trim() || "") ? form.unique_email!.toString().trim() : null
            },
            {
                onSuccess: () => {
                    setEditingId(null);
                    setEditError(null);
                },
                onError: (e: any) => {
                    setEditError(e?.response?.data?.message || "Errore durante il salvataggio");
                },
            }
        );
    };

    const [isCreateOpen, setIsCreateOpen] = useState<boolean>(false);
    const [createError, setCreateError] = useState<string | null>(null);
    const [createForm, setCreateForm] = useState<{
        ris_id: number | "";
        cisco_id: string | null;
        company_name: string;
        contact_name: string;
        address: string | null;
        city: string | null;
        zip: string | null;
        province: string | null;
        vat: string | null;
        tax_id: string | null;
        payment_code: string;
        payment_method: string;
        unique_email: string;
    }>({
        ris_id: "",
        cisco_id: "",
        company_name: "",
        contact_name: "",
        address: "",
        city: "",
        zip: "",
        province: "",
        vat: "",
        tax_id: "",
        payment_code: "",
        payment_method: "",
        unique_email: "",
    });

    const openCreateDialog = (prefill?: Partial<typeof createForm>) => {
        setCreateError(null);
        setCreateForm(prev => ({
            ris_id: "",
            cisco_id: "",
            company_name: "",
            contact_name: "",
            address: "",
            city: "",
            zip: "",
            province: "",
            vat: "",
            tax_id: "",
            payment_code: "",
            payment_method: "",
            unique_email: "",
            ...prefill,
        }));
        setIsCreateOpen(true);
    };

    const closeCreateDialog = () => {
        setIsCreateOpen(false);
        setCreateError(null);
    };

    const submitCreate = () => {
        setCreateError(null);

        if (!createForm.company_name?.toString().trim()) {
            setCreateError("Il campo Ragione Sociale è obbligatorio.");
            return;
        }

        if (!createForm.vat || !createForm.vat.toString().trim()) {
            setCreateError("Il campo P.IVA è obbligatorio.");
            return;
        }
        if (!createForm.payment_code?.trim()) {
            setCreateError("Il campo Codice pagamento è obbligatorio.");
            return;
        }

        const selectedPaymentMethod = paymentMethods?.find((pm: any) => pm.official_code === createForm.payment_code);
        const paymentCodeToSend = selectedPaymentMethod ? selectedPaymentMethod.code.toString() : (createForm.payment_code);

        const payload: any = {
            ris_id: Number(createForm.ris_id) || null,
            cisco_id: Number(createForm.cisco_id) || null,
            company_name: createForm.company_name?.trim(),
            contact_name: createForm.contact_name?.trim() || "",
            address: createForm.address?.toString() || null,
            city: createForm.city?.toString() || null,
            zip: createForm.zip?.toString() || null,
            province: createForm.province?.toString() || null,
            vat: createForm.vat?.toString(),
            tax_id: createForm.tax_id?.toString() || null,
            payment_code: paymentCodeToSend,
            payment_method: createForm.payment_method?.trim() || "",
            unique_email: (createForm.unique_email?.toString().trim() || "") ? createForm.unique_email.toString().trim() : null,
        };
        if (createForm.ris_id !== "") {
            payload.ris_id = Number(createForm.ris_id);
        }

        createBillingMutation.mutate(
            payload,
            {
                onSuccess: () => {
                    setIsCreateOpen(false);
                    setCreateError(null);
                },
                onError: (e: any) => {
                    setCreateError(e?.response?.data?.message || "Errore durante il salvataggio");
                },
            }
        );
    };

    const navigate = useNavigate();

    return (
        <div className="p-4">
            <div className="ml-auto flex items-center gap-1 py-3">
                <button
                    type="button"
                    className={`inline-flex items-center gap-3 px-3 py-2 rounded-md border bg-mcia_orange text-white hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 transition`}
                    onClick={() => navigate("/home")}
                    title="Mostra la lista dei recap"
                >
                    <IoPeopleOutline className="text-lg" />
                    <span>Riepilogo</span>
                </button>
            </div>
            <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Filtra per ragione sociale / referente</label>
                <input
                    type="text"
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    placeholder="Digita per filtrare…"
                    className="w-full max-w-md border px-3 py-2 rounded"
                />
            </div>

            {isLoading ? (
                <Loading />
            ) : isError ? (
                <div className="text-red-500">
                    Errore: {error?.response?.data?.message || "Unknown error"}
                </div>
            ) : data && data.length > 0 ? (
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                    className="p-4 border rounded shadow w-full"
                >
                    <div className="flex items-center justify-between mb-2">
                        <h2 className="text-lg font-bold">Ristoratori</h2>
                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                onClick={() => openCreateDialog()}
                                className="inline-flex items-center gap-2 px-3 py-1 rounded border bg-gray-100 hover:bg-gray-200"
                                title="Inserisci nuovo ristoratore"
                            >
                                <IoDownloadOutline />
                                <span>Inserisci</span>
                            </button>
                        </div>
                    </div>
                    <div className="overflow-x-auto border rounded w-full">
                        <table className="min-w-[900px] w-full divide-y divide-gray-300 text-sm">
                            <thead className="bg-gray-100">
                                <tr className="bg-mcia_orange text-white hover:text-black">
                                    <th className="px-2 py-2 w-8"></th>
                                    <th className="px-4 py-2 text-left font-semibold">Ragione Sociale</th>
                                    <th className="px-4 py-2 text-left font-semibold">Referente</th>
                                    <th className="px-4 py-2 text-left font-semibold">P.IVA</th>
                                    <th className="px-4 py-2 text-left font-semibold">Codice Pagamento</th>
                                    <th className="px-4 py-2 text-left font-semibold">Metodo Pagamento</th>
                                    <th className="px-4 py-2 text-left font-semibold">Email Univoca</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-300">
                                {filtered.map((row: any) => {
                                    const isExpanded = expandedRows[row.id];
                                    return (
                                        <React.Fragment key={row.id}>
                                            <tr
                                                onClick={() => toggleRow(row.id)}
                                                className="cursor-pointer hover:bg-mcia_salmon transition odd:bg-white even:bg-gray-200"
                                            >
                                                <td className="px-2 py-2 w-8" onClick={(e) => { e.stopPropagation(); toggleRow(row.id); }}>
                                                    {/* Simple chevron */}
                                                    <ExpandIcon expanded={isExpanded} />
                                                </td>
                                                <td className="px-4 py-2 font-medium">{row.company_name}</td>
                                                <td className="px-4 py-2">{row.contact_name || "-"}</td>
                                                <td className="px-4 py-2">{row.vat || "-"}</td>
                                                <td className="px-4 py-2">{row.payment_code || "-"}</td>
                                                <td className="px-4 py-2">{row.payment_method || "-"}</td>
                                                <td className="px-4 py-2">{row.unique_email || "-"}</td>
                                            </tr>

                                            <AnimatePresence>
                                                {isExpanded && (
                                                    <motion.tr
                                                        initial={{ opacity: 0, height: 0 }}
                                                        animate={{ opacity: 1, height: "auto" }}
                                                        exit={{ opacity: 0, height: 0 }}
                                                        transition={{ duration: 0.25 }}
                                                        className="bg-gray-50"
                                                    >
                                                        <td colSpan={7} className="px-4 py-3">
                                                            {editingId === row.id ? (
                                                                <BillingEditForm
                                                                    form={form as any}
                                                                    setForm={setForm as any}
                                                                    error={editError}
                                                                    onSaveClick={(e) => { e.stopPropagation(); submitEdit(row.id); }}
                                                                    onCancelClick={(e) => { e.stopPropagation(); cancelEdit(); }}
                                                                    paymentMethods={pmList}
                                                                    isPmLoading={isPmLoading}
                                                                    isPmError={isPmError}
                                                                    isSaving={updateBillingMutation.isLoading}
                                                                />
                                                            ) : (
                                                                <div className="flex items-center justify-between">
                                                                    <div className="space-y-1 text-sm">
                                                                        <p><span className="text-xs text-mcia_orange"><strong>Ragione Sociale</strong></span>: {row.company_name}</p>
                                                                        <p><span className="text-xs text-mcia_orange"><strong>Cisco Id</strong></span>: {row.cisco_id || "N/D"}</p>
                                                                        <p><span className="text-xs text-mcia_orange"><strong>Indirizzo</strong></span>: {row.address || "N/D"}</p>
                                                                        <p><span className="text-xs text-mcia_orange"><strong>Città</strong></span>: {row.city || "N/D"}</p>
                                                                        <p><span className="text-xs text-mcia_orange"><strong>CAP</strong></span>: {row.zip || "N/D"}</p>
                                                                        <p><span className="text-xs text-mcia_orange"><strong>Provincia</strong></span>: {row.province || "N/D"}</p>
                                                                        <p><span className="text-xs text-mcia_orange"><strong>Cod. Fiscale</strong></span>: {row.tax_id || "N/D"}</p>
                                                                        <p><span className="text-xs text-mcia_orange"><strong>Email Univoca</strong></span>: {row.unique_email || "N/D"}</p>
                                                                    </div>
                                                                    <div>
                                                                        <button
                                                                            type="button"
                                                                            className="px-3 py-1 rounded border bg-gray-100 hover:bg-gray-200"
                                                                            onClick={(e) => { e.stopPropagation(); startEdit(row); }}
                                                                        >
                                                                            Modifica dati fatturazione
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </td>
                                                    </motion.tr>
                                                )}
                                            </AnimatePresence>
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </motion.div>
            ) : (
                <div className="text-gray-500">Nessun dato disponibile</div>
            )}
            {isCreateOpen && (
                <CreateBillingModal
                    open={isCreateOpen}
                    form={createForm as any}
                    setForm={setCreateForm as any}
                    error={createError}
                    onClose={closeCreateDialog}
                    onSubmit={submitCreate}
                    paymentMethods={pmList}
                    isPmLoading={isPmLoading}
                    isPmError={isPmError}
                    isSaving={createBillingMutation.isLoading}
                    restaurants={restaurants}
                    restaurantsLoading={isRestaurantsLoading}
                    restaurantsError={isRestaurantsError}
                    readOnlyRisId={false}
                />
            )}
        </div>
    );
}

export default BillingDataPage;